import { Progress } from "@/components/ui/progress";
import { BACKGROUND_MULTIPLE_COLORS, MULTIPLE_COLORS } from "@roman/shared/constants/predictionChart";
import { useIsMobile } from "@roman/shared/hooks/soccer/useIsMobile";
import type { PredictionMarketChoiceWithProbability } from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import { isMobileAgent } from "@roman/shared/utils/platform";
import {
  PredictionMarketType,
  getPredictionMarketType,
  getYesNoChoices,
} from "@roman/shared/utils/predictionMarket/utils";
import { ChoiceCard } from "./ChoiceCard";
import { YesNoCard } from "./YesNoCard";

interface MultipleChoicesProps {
  choices: PredictionMarketChoiceWithProbability[];
  onButtonClick: (choiceId: string) => void;
  onCommentClick?: () => void;
  outlined?: boolean;
  onlyProbability?: boolean;
  showAll?: boolean;
  showTop?: boolean;
  isFixedYesNo?: boolean;
  isShowLabel?: boolean;
  isShowProbability?: boolean;
  isScrollable?: boolean;
  isShared?: boolean;
}

export const MultipleChoices = ({
  choices,
  onButtonClick,
  onCommentClick,
  outlined = false,
  onlyProbability = false,
  showAll = false,
  showTop = false,
  isFixedYesNo = false,
  isShowLabel = false,
  isShowProbability = true,
  isScrollable = true,
  isShared = false,
}: MultipleChoicesProps) => {
  const predictionMarketType = getPredictionMarketType(choices.map((c) => ({ name: c.name })));

  const isMobile = useIsMobile();

  if (predictionMarketType === PredictionMarketType.TwoChoice) {
    const { yesChoice, noChoice } = getYesNoChoices(choices);

    if (isFixedYesNo) {
      return (
        <div
          className={cn(
            "relative w-full md:grid md:grid-cols-4 md:gap-1 lg:gap-3",
            isFixedYesNo ? "fixed left-0 w-full z-30 h-[70px] " : "",
            isMobile ? "bottom-[76px]" : "bottom-[0px]",
          )}
        >
          <div className="md:col-start-2 xl:col-start-2 md:col-span-3 xl:col-span-2 bg-white px-5 py-[10px] border-t border-b border-solid border-gray-200 flex justify-center items-center gap-3">
            <YesNoCard
              className="flex flex-col items-center justify-center gap-1 text-[17px]"
              isFixedYesNo
              id={yesChoice.id}
              label={yesChoice.name}
              probability={yesChoice.probability}
              onClick={onButtonClick}
              outlined={outlined}
              onlyProbability={onlyProbability}
              light={outlined}
              isShowProbability={isShowProbability}
              isYesChoice={true}
            />
            <YesNoCard
              className="flex flex-col items-center justify-center gap-1 text-[17px]"
              isFixedYesNo
              id={noChoice.id}
              label={noChoice.name}
              probability={noChoice.probability}
              onClick={onButtonClick}
              outlined={outlined}
              onlyProbability={onlyProbability}
              light={outlined}
              isShowProbability={isShowProbability}
            />
          </div>
        </div>
      );
    }

    if (isShared) {
      return (
        <div className="md:col-start-2 xl:col-start-2 md:col-span-3 xl:col-span-2 bg-white flex justify-center items-center gap-3">
          <YesNoCard
            className="flex flex-row items-center justify-center gap-1 text-[13px] font-bold"
            isFixedYesNo={true}
            id={yesChoice.id}
            label={yesChoice.name}
            probability={yesChoice.probability}
            onClick={onButtonClick}
            outlined={outlined}
            onlyProbability={onlyProbability}
            light={outlined}
            isShowProbability={isShowProbability}
            isYesChoice={true}
          />
          <YesNoCard
            className="flex flex-row items-center justify-center gap-1 text-[13px] font-bold"
            isFixedYesNo={true}
            id={noChoice.id}
            label={noChoice.name}
            probability={noChoice.probability}
            onClick={onButtonClick}
            outlined={outlined}
            onlyProbability={onlyProbability}
            light={outlined}
            isShowProbability={isShowProbability}
          />
        </div>
      );
    }
    const getPercentage = (probability: number, color: string, lowColor: string) => {
      const percent = probability * 100;
      if (percent < 1) return { display: "<1", style: { color: lowColor } };
      if (percent === 100) return { display: "99", style: { color: "white" } };
      return {
        display: Math.round(percent),
        style: { WebkitTextStroke: `2px ${color}`, paintOrder: "stroke" },
      };
    };

    const yes = getPercentage(yesChoice.probability, "#00AFCE", "#E6F7FA");
    const no = getPercentage(noChoice.probability, "#FF7FD2", "#FFF2FB");

    // Mobile platform detection for platform-specific styling
    const isMobileUserAgent = isMobileAgent();

    return (
      <div className="flex flex-col">
        <div className="w-full h-4 relative mt-[2px]">
          <div className="z-[3] left-[7px] absolute flex items-center justify-center" style={yes.style}>
            <p
              className={cn(
                "inline-flex text-white font-bold font-roboto text-[14px] leading-none px-1 h-4",
                isMobileUserAgent ? "items-end" : "items-center",
              )}
            >
              {yes.display}%
            </p>
          </div>
          <div className="z-[3] right-[7px] absolute flex items-center justify-center" style={{ ...no.style }}>
            <p
              className={cn(
                "inline-flex text-white font-bold font-roboto text-[14px] leading-none px-1 h-4",
                isMobileUserAgent ? "items-end" : "items-center",
              )}
            >
              {no.display}%
            </p>
          </div>
          <Progress
            tiltGap={true}
            value={Number((yesChoice.probability * 100).toFixed(2))}
            className="w-full h-full rounded-sm"
            indicatorColor="bg-[#00AFCE]"
            trackColor="bg-[#FF7FD2]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-[9px]">
          {[yesChoice, noChoice].map((choice, idx) => (
            <YesNoCard
              key={choice.id}
              id={choice.id}
              label={choice.name}
              probability={choice.probability}
              onClick={onButtonClick}
              outlined={outlined}
              onlyProbability={onlyProbability}
              light={outlined}
              isShowProbability={false}
              isYesChoice={idx === 0}
              className="min-h-10 h-10 overflow-hidden text-[18px]"
            />
          ))}
        </div>
      </div>
    );
  }
  choices.sort((a, b) => b.probability - a.probability);
  if (!showTop) {
    return (
      <div
        className={cn(
          "grid gap-1 overflow-hidden hide-scrollbar",
          showAll ? "h-full" : "h-[90px]",
          isScrollable ? "overflow-y-scroll" : "overflow-hidden",
        )}
      >
        {isShowLabel && (
          <div className="font-noto-sans-jp text-[14px] font-medium text-black leading-[1.2] mb-[11px]">
            答えを選択してください
          </div>
        )}
        {choices.map((choice, index) => (
          <ChoiceCard
            key={choice.id}
            id={choice.id}
            name={choice.name}
            probability={choice.probability}
            commentCount={choice.commentCount}
            color={MULTIPLE_COLORS[index % MULTIPLE_COLORS.length]}
            backgroundColor={BACKGROUND_MULTIPLE_COLORS[index % BACKGROUND_MULTIPLE_COLORS.length]}
            onClick={onButtonClick}
            onCommentClick={onCommentClick}
          />
        ))}
      </div>
    );
  }

  // If showTop is true, show only the top choice (highest probability)
  const topChoice = [...choices].sort((a, b) => b.probability - a.probability)[0];
  const topIndex = choices.findIndex((c) => c.id === topChoice.id);
  return (
    <div className={cn("grid gap-1 overflow-y-scroll hide-scrollbar h-[34.5px]")}>
      <ChoiceCard
        key={topChoice.id}
        id={topChoice.id}
        name={topChoice.name}
        probability={topChoice.probability}
        commentCount={topChoice.commentCount}
        color={MULTIPLE_COLORS[topIndex % MULTIPLE_COLORS.length]}
        backgroundColor={BACKGROUND_MULTIPLE_COLORS[topIndex % BACKGROUND_MULTIPLE_COLORS.length]}
        onClick={onButtonClick}
      />
    </div>
  );
};
